/**
 * Read-only quote loader for the Pricing Engine.
 * Do not insert, update, or recalculate stored totals from here.
 */
import { directSupabase } from '../../lib/supabaseClient';
import type { JobInputs, JobLine } from './pricingEngine.model';
import { chooseWorkspacePricing, type SurveyBinding } from './pricingEngine.source';

export type QuoteSearchHit = {
  documentId: string;
  lifecycleRef: string;
  kind: string;
  status: string;
  title: string;
  customerName: string;
  projectName: string | null;
  grossTotal: number | null;
  createdAt: string;
};

export type QuoteVersionOption = {
  id: string;
  number: number;
  active: boolean;
  title: string | null;
};

export type LoadedQuote = {
  documentId: string;
  projectId: string | null;
  projectName: string | null;
  lifecycleRef: string;
  kind: string;
  status: string;
  title: string;
  customerName: string;
  storedNet: number | null;
  storedGross: number | null;
  storedDeposit: number | null;
  versionId: string | null;
  versionNumber: number | null;
  versionActive: boolean;
  liveVersionId: string | null;
  /** Legacy quotes list document versions. Workspace quotes price one survey sheet and leave this empty. */
  versions: QuoteVersionOption[];
  /** `legacy` keeps the version list. `final` or `first` is the workspace sheet being priced. */
  pricingSource: 'legacy' | 'first' | 'final';
  /** The workspace sheet exists but has no pricing record on this quote yet. */
  surveyUnpriced: boolean;
  inputs: JobInputs;
};

type DocRow = {
  id: string;
  title?: string | null;
  lifecycle_ref?: string | null;
  kind?: string | null;
  status?: string | null;
  project_id?: string | null;
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  created_at?: string | null;
  gross_total?: number | null;
  net_total?: number | null;
  deposit_amount?: number | null;
  active_version_id?: string | null;
  admin_pct?: number | null;
  admin_cap?: number | null;
  contingency_pct?: number | null;
  profit_pct?: number | null;
  vat_enabled?: boolean | null;
  vat_pct?: number | null;
  has_deposit?: boolean | null;
  deposit_percentage?: number | null;
  project_days?: number | null;
  project_hours?: number | null;
};

const DOC_SEARCH_COLUMNS = 'id, title, lifecycle_ref, kind, status, project_id, customer_first_name, customer_last_name, created_at, gross_total';

function safeTerm(term: string): string {
  return term.replace(/[%_,.()]/g, ' ').replace(/\s+/g, ' ').trim();
}

function customerName(row: { customer_first_name?: string | null; customer_last_name?: string | null }): string {
  return [row.customer_first_name, row.customer_last_name].filter(Boolean).join(' ').trim();
}

function asNumber(value: unknown, fallback = 0): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function asNumberOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function mapLine(row: Record<string, unknown>, index: number): JobLine {
  return {
    id: String(row.id || `line-${index}`),
    lineKind: String(row.line_kind || 'other'),
    name: String(row.name || 'Untitled line'),
    description: row.description == null ? null : String(row.description),
    qty: asNumber(row.qty),
    unit: row.unit == null ? null : String(row.unit),
    unitPrice: asNumber(row.unit_price),
    lineTotal: row.line_total == null ? null : asNumber(row.line_total),
    vatInclusive: !!row.vat_inclusive,
    labourDays: asNumberOrNull(row.labour_days),
    labourHours: asNumberOrNull(row.labour_hours),
    sortOrder: asNumberOrNull(row.sort_order),
  };
}

function inputsFrom(header: DocRow, lines: JobLine[]): JobInputs {
  return {
    lines,
    adminPct: asNumber(header.admin_pct),
    adminCap: asNumber(header.admin_cap),
    contingencyPct: asNumber(header.contingency_pct),
    profitPct: asNumber(header.profit_pct),
    vatEnabled: !!header.vat_enabled,
    vatPct: header.vat_pct == null ? 0.2 : asNumber(header.vat_pct, 0.2),
    hasDeposit: !!header.has_deposit,
    depositAmount: asNumber(header.deposit_amount),
    depositPercentage: asNumberOrNull(header.deposit_percentage),
    projectDays: asNumberOrNull(header.project_days),
    projectHours: asNumberOrNull(header.project_hours),
  };
}

async function projectNames(ids: string[]): Promise<Map<string, string>> {
  const unique = [...new Set(ids.filter(Boolean))];
  const names = new Map<string, string>();
  if (unique.length === 0) return names;
  const { data, error } = await directSupabase
    .from('projects')
    .select('id, project_name')
    .in('id', unique);
  if (error) return names;
  for (const row of data || []) {
    const id = String((row as { id?: string }).id || '');
    const name = String((row as { project_name?: string }).project_name || '').trim();
    if (id && name) names.set(id, name);
  }
  return names;
}

function toHit(row: DocRow, names: Map<string, string>): QuoteSearchHit {
  const projectId = row.project_id ? String(row.project_id) : '';
  return {
    documentId: String(row.id),
    lifecycleRef: String(row.lifecycle_ref || ''),
    kind: String(row.kind || ''),
    status: String(row.status || ''),
    title: String(row.title || 'Untitled quote'),
    customerName: customerName(row),
    projectName: projectId ? names.get(projectId) || null : null,
    grossTotal: asNumberOrNull(row.gross_total),
    createdAt: String(row.created_at || ''),
  };
}

export async function searchQuotes(term: string): Promise<QuoteSearchHit[]> {
  const safe = safeTerm(term);
  if (safe.length < 2) return [];

  const { data: docs, error } = await directSupabase
    .from('documents')
    .select(DOC_SEARCH_COLUMNS)
    .in('kind', ['Estimate', 'Quotation'])
    .neq('status', 'void')
    .or(`lifecycle_ref.ilike.%${safe}%,title.ilike.%${safe}%,customer_first_name.ilike.%${safe}%,customer_last_name.ilike.%${safe}%`)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw new Error(error.message);

  const { data: projects } = await directSupabase
    .from('projects')
    .select('id, project_name, customer_name')
    .or(`project_name.ilike.%${safe}%,customer_name.ilike.%${safe}%`)
    .limit(8);

  const projectIds = (projects || []).map((row) => String((row as { id?: string }).id || '')).filter(Boolean);
  let projectDocs: DocRow[] = [];
  if (projectIds.length > 0) {
    const { data, error: projectDocsError } = await directSupabase
      .from('documents')
      .select(DOC_SEARCH_COLUMNS)
      .in('project_id', projectIds)
      .in('kind', ['Estimate', 'Quotation'])
      .neq('status', 'void')
      .order('created_at', { ascending: false })
      .limit(20);
    if (projectDocsError) throw new Error(projectDocsError.message);
    projectDocs = (data || []) as DocRow[];
  }

  const merged = new Map<string, DocRow>();
  for (const row of [...((docs || []) as DocRow[]), ...projectDocs]) {
    merged.set(String(row.id), row);
  }
  const names = await projectNames([...merged.values()].map((row) => String(row.project_id || '')));
  for (const row of projects || []) {
    const id = String((row as { id?: string }).id || '');
    const name = String((row as { project_name?: string }).project_name || '').trim();
    if (id && name) names.set(id, name);
  }

  return [...merged.values()]
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
    .slice(0, 20)
    .map((row) => toHit(row, names));
}

export async function loadQuote(documentId: string, versionId?: string | null): Promise<LoadedQuote> {
  const { data, error } = await directSupabase
    .from('documents')
    .select('*')
    .eq('id', documentId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error('Quote not found');
  const doc = data as DocRow;
  if (doc.kind !== 'Estimate' && doc.kind !== 'Quotation') {
    throw new Error('Pricing Engine reads estimates and quotations.');
  }

  const { data: versionRows, error: versionError } = await directSupabase
    .from('document_versions')
    .select('id, version_number, is_active, title, admin_pct, admin_cap, contingency_pct, profit_pct, vat_enabled, vat_pct, has_deposit, deposit_amount, deposit_percentage, project_days, project_hours')
    .eq('document_id', documentId)
    .order('version_number', { ascending: true });
  if (versionError) throw new Error(versionError.message);

  const versions: QuoteVersionOption[] = (versionRows || []).map((row) => ({
    id: String((row as { id: string }).id),
    number: Number((row as { version_number?: number }).version_number || 0),
    active: !!(row as { is_active?: boolean }).is_active,
    title: ((row as { title?: string | null }).title || '').trim() || null,
  }));

  const pointedId = String(doc.active_version_id || '');
  const live = versions.find((version) => version.active) || versions.find((version) => version.id === pointedId) || null;
  const projectId = doc.project_id ? String(doc.project_id) : null;
  const choice = chooseWorkspacePricing(
    projectId ? await surveyBindings(projectId) : [],
    versions.map((version) => version.id),
  );

  const requested = versionId ? versions.find((version) => version.id === versionId) : null;
  const chosen = choice.mode === 'survey'
    ? (choice.versionId ? versions.find((version) => version.id === choice.versionId) || null : null)
    : (requested || live || versions[versions.length - 1] || null);
  const surveyUnpriced = choice.mode === 'survey' && !chosen;

  let header: DocRow = doc;
  let lineRows: Record<string, unknown>[] = [];
  if (chosen) {
    const versionHeader = (versionRows || []).find((row) => String((row as { id: string }).id) === chosen.id) as DocRow | undefined;
    if (versionHeader) header = { ...doc, ...versionHeader, id: doc.id };
    const { data: lines, error: lineError } = await directSupabase
      .from('document_version_lines')
      .select('id, line_kind, name, description, qty, unit, unit_price, line_total, vat_inclusive, labour_days, labour_hours, sort_order')
      .eq('version_id', chosen.id)
      .order('sort_order', { ascending: true });
    if (lineError) throw new Error(lineError.message);
    lineRows = (lines || []) as Record<string, unknown>[];
  } else if (!surveyUnpriced) {
    const { data: lines, error: lineError } = await directSupabase
      .from('document_lines')
      .select('id, line_kind, name, description, qty, unit, unit_price, line_total, vat_inclusive, labour_days, labour_hours, sort_order')
      .eq('document_id', documentId)
      .order('sort_order', { ascending: true });
    if (lineError) throw new Error(lineError.message);
    lineRows = (lines || []) as Record<string, unknown>[];
  }

  const names = await projectNames([projectId || '']);
  const liveId = live?.id || pointedId || null;
  const versionActive = choice.mode === 'survey' || chosen == null || !liveId || chosen.id === liveId;

  return {
    documentId: String(doc.id),
    projectId,
    projectName: projectId ? names.get(projectId) || null : null,
    lifecycleRef: String(doc.lifecycle_ref || ''),
    kind: String(doc.kind || ''),
    status: String(doc.status || ''),
    title: String(header.title || doc.title || 'Untitled quote'),
    customerName: customerName(doc),
    storedNet: asNumberOrNull(doc.net_total),
    storedGross: asNumberOrNull(doc.gross_total),
    storedDeposit: asNumberOrNull(doc.deposit_amount),
    versionId: chosen?.id || null,
    versionNumber: chosen?.number ?? null,
    versionActive,
    liveVersionId: liveId,
    versions: choice.mode === 'survey' ? [] : versions,
    pricingSource: choice.mode === 'survey' ? choice.survey : 'legacy',
    surveyUnpriced,
    inputs: inputsFrom(header, lineRows.map(mapLine)),
  };
}

async function surveyBindings(projectId: string): Promise<SurveyBinding[]> {
  const { data, error } = await directSupabase
    .from('visual_designer_surveys')
    .select('kind, option_id, document_version_id')
    .eq('project_id', projectId);
  if (error) throw new Error(error.message);
  return (data || []).map((row) => ({
    kind: String((row as { kind?: string }).kind || ''),
    optionId: (row as { option_id?: string | null }).option_id
      ? String((row as { option_id?: string | null }).option_id)
      : null,
    documentVersionId: (row as { document_version_id?: string | null }).document_version_id
      ? String((row as { document_version_id?: string | null }).document_version_id)
      : null,
  }));
}
